import {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Search,
  Smartphone,
  Monitor,
  MonitorSmartphone,
  GaugeCircle,
  FolderKanban,
  Link2,
  CircleAlert,
  AlertTriangle,
  CheckCircle2,
  Info,
} from "lucide-react";
import { isCancel } from "axios";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import styles from "./PageSpeedPage.module.css";
import Button from "../../components/UI/Button/Button";
import ProjectPickerCarousel from "../../components/Layout/ProjectPickerCarousel/ProjectPickerCarousel";
import { getPlanStableId } from "../../data/dashboard-projects-dummy";
import { useAuth } from "../../context/authContext";
import {
  getUserSubscriptionPlans,
  getSubscriptionPlans,
} from "../../services/subscription-plan-service";
import {
  analyzePageSpeedBoth,
  getSavedPageSpeed,
} from "../../services/pagespeed-service";

function urlFromPlan(plan) {
  const raw = (plan?.projectUrl || "").trim();
  if (!raw) return "https://";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function normalizeUrlForCompare(raw) {
  try {
    const u = new URL(String(raw).trim());
    u.hash = "";
    let path = u.pathname;
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return `${u.protocol}//${u.hostname.toLowerCase()}${path}${u.search}`;
  } catch {
    return String(raw).trim();
  }
}

function htmlToText(html) {
  if (!html || typeof window === "undefined") return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent?.trim() || "";
  } catch {
    return "";
  }
}

function scoreTierAudit(score, scoreDisplayMode) {
  const na =
    scoreDisplayMode === "notApplicable" ||
    scoreDisplayMode === "not_applicable";
  if (score == null || na) return "neutral";
  if (score >= 0.9) return "good";
  if (score >= 0.5) return "avg";
  return "poor";
}

function scoreTier100(n) {
  if (n == null) return "neutral";
  if (n >= 90) return "good";
  if (n >= 50) return "avg";
  return "poor";
}

function sortAudits(list) {
  return [...list].sort((a, b) => {
    const sa = a.score;
    const sb = b.score;
    if (sa == null && sb == null) return 0;
    if (sa == null) return 1;
    if (sb == null) return -1;
    return sa - sb;
  });
}

const CLIENT_AUDIT_INSIGHT_LIMIT = 5;

function AuditInsightIcon({ tier }) {
  const common = { size: 18, strokeWidth: 2, "aria-hidden": true };
  if (tier === "poor") return <CircleAlert {...common} />;
  if (tier === "avg") return <AlertTriangle {...common} />;
  if (tier === "good") return <CheckCircle2 {...common} />;
  return <Info {...common} />;
}

const COLORS = {
  good: "#0cce6b",
  avg: "#ffa400",
  poor: "#ff4e42",
  neutral: "#dadce0",
};

const METRIC_ORDER = [
  "first-contentful-paint",
  "total-blocking-time",
  "speed-index",
  "largest-contentful-paint",
  "cumulative-layout-shift",
];

const ZERO_CATS = {
  performance: 0,
  accessibility: 0,
  bestPractices: 0,
  seo: 0,
};

const METRIC_I18N = {
  "first-contentful-paint": "seoPage.metricFcp",
  "largest-contentful-paint": "seoPage.metricLcp",
  "total-blocking-time": "seoPage.metricTbt",
  "cumulative-layout-shift": "seoPage.metricCls",
  "speed-index": "seoPage.metricSi",
};

const INSIGHT_ORDER = [
  "performance",
  "accessibility",
  "bestPractices",
  "seo",
];

const INSIGHT_LABEL = {
  performance: "seoPage.performance",
  accessibility: "seoPage.accessibility",
  bestPractices: "seoPage.bestPractices",
  seo: "seoPage.seoCategory",
};

const INSIGHT_AUDITS_TITLE = {
  performance: "seoPage.auditsHeadingPerformance",
  accessibility: "seoPage.auditsHeadingAccessibility",
  bestPractices: "seoPage.auditsHeadingBestPractices",
  seo: "seoPage.auditsHeadingSeo",
};

function buildPlaceholderMetrics(t) {
  return METRIC_ORDER.map((id) => ({
    id,
    title: t(METRIC_I18N[id]),
    displayValue: "—",
    score: null,
    scoreDisplayMode: "not_applicable",
  }));
}

function orderMetrics(metrics) {
  if (!metrics?.length) return [];
  const byId = Object.fromEntries(metrics.map((m) => [m.id, m]));
  return METRIC_ORDER.map((id) => byId[id]).filter(Boolean);
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

const PSI_RING_ANIM_MS = 900;

function PsiRing({ score, size, label, ariaLabel }) {
  const targetPct =
    score != null ? Math.max(0, Math.min(100, Math.round(score))) : null;
  const arcEnd = targetPct == null ? 0 : targetPct;

  const [animPct, setAnimPct] = useState(0);
  const animRef = useRef(0);
  const rafRef = useRef(null);

  useLayoutEffect(() => {
    const from = animRef.current;
    const to = arcEnd;
    if (Math.abs(to - from) < 0.05) {
      animRef.current = to;
      setAnimPct(to);
      return undefined;
    }
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / PSI_RING_ANIM_MS);
      const v = from + (to - from) * easeOutCubic(t);
      animRef.current = v;
      setAnimPct(v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        animRef.current = to;
        setAnimPct(to);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [arcEnd]);

  const displayTier = scoreTier100(targetPct);
  const stroke = COLORS[displayTier] || COLORS.neutral;
  const dim = size === "large" ? 168 : 76;
  const r = size === "large" ? 64 : 28;
  const sw = size === "large" ? 7 : 3.5;
  const cx = dim / 2;
  const cy = dim / 2;
  const c = 2 * Math.PI * r;
  const dash = (animPct / 100) * c;
  const gap = c - dash;

  const centerLabel =
    targetPct == null ? "—" : String(Math.round(animPct));

  const ringInner = (
    <>
      <svg
        width={dim}
        height={dim}
        viewBox={`0 0 ${dim} ${dim}`}
        className={styles.psiRingSvg}
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#e8eaed"
          strokeWidth={sw}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${gap}`}
          transform={`rotate(-90 ${cx} ${cy})`}
          className={styles.psiRingProgressArc}
        />
      </svg>
      <span
        className={
          size === "large" ? styles.psiRingLargeNum : styles.psiRingMiniNum
        }
      >
        {centerLabel}
      </span>
    </>
  );

  if (size === "large") {
    return (
      <div className={styles.psiRingLargeWrap} aria-label={ariaLabel}>
        <div className={styles.psiRingLargeInner}>{ringInner}</div>
        {label ? <span className={styles.psiRingLargeLbl}>{label}</span> : null}
      </div>
    );
  }

  return (
    <div className={styles.psiRingMiniWrap} aria-label={ariaLabel}>
      {ringInner}
    </div>
  );
}

function MetricDot({ tier }) {
  return (
    <span
      className={`${styles.metricDot} ${styles[`metricDot_${tier}`]}`}
      aria-hidden
    />
  );
}

export default function PageSpeedPage() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [inputSource, setInputSource] = useState("project");
  const [url, setUrl] = useState("https://");
  const [strategy, setStrategy] = useState("mobile");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const abortRef = useRef(null);
  const savedFetchRef = useRef(null);
  const loadingRef = useRef(false);
  const hadSubscriptionPlansRef = useRef(false);
  const reportDetailRef = useRef(null);
  const [insightCategory, setInsightCategory] = useState("performance");

  const selectInsightCategory = useCallback((cat) => {
    setInsightCategory(cat);
    requestAnimationFrame(() => {
      reportDetailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const fetchPlans = useCallback(async () => {
    try {
      if (!user) {
        setPlans([]);
        return;
      }
      const isStaff =
        user.role === "admin" || user.role === "owner";
      if (isStaff) {
        const res = await getSubscriptionPlans();
        const list =
          res.success && Array.isArray(res.data) ? res.data : [];
        setPlans(list);
      } else {
        const res = await getUserSubscriptionPlans();
        const list = res.success ? res.data || [] : [];
        setPlans(list);
      }
    } catch {
      setPlans([]);
    }
  }, [user]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans, location.key]);

  useEffect(() => {
    if (!plans.length) {
      setSelectedProjectId(null);
      setInputSource("custom");
      hadSubscriptionPlansRef.current = false;
      return;
    }
    if (!hadSubscriptionPlansRef.current) {
      setInputSource("project");
    }
    hadSubscriptionPlansRef.current = true;
    setSelectedProjectId((prev) => {
      const ids = plans.map((p, i) => getPlanStableId(p, i));
      if (prev && ids.includes(prev)) return prev;
      return ids[0] ?? null;
    });
  }, [plans]);

  useEffect(() => {
    if (inputSource !== "project" || !selectedProjectId || !plans.length) {
      return;
    }
    const idx = plans.findIndex(
      (p, i) => getPlanStableId(p, i) === selectedProjectId
    );
    if (idx < 0) return;
    const u = urlFromPlan(plans[idx]);
    if (u !== "https://") setUrl(u);
  }, [inputSource, selectedProjectId, plans]);

  const run = useCallback(
    async (e) => {
      e?.preventDefault?.();
      const trimmed = url.trim();
      if (!trimmed || trimmed === "https://" || trimmed === "http://") {
        setError(t("seoPage.errors.urlRequired"));
        return;
      }
      try {
        new URL(trimmed);
      } catch {
        setError(t("seoPage.errors.urlInvalid"));
        return;
      }
      if (!/^https?:\/\//i.test(trimmed)) {
        setError(t("seoPage.errors.urlInvalid"));
        return;
      }

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setError(null);
      setLoading(true);
      try {
        const data = await analyzePageSpeedBoth(trimmed, {
          signal: ac.signal,
        });
        setResult({ ...data, source: "live" });
      } catch (err) {
        if (isCancel(err)) return;
        const msg =
          err.response?.data?.message ||
          err.message ||
          t("seoPage.errors.generic");
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [url, t]
  );

  useEffect(() => {
    const trimmed = url.trim();
    if (!/^https?:\/\/.+/i.test(trimmed)) {
      return undefined;
    }

    savedFetchRef.current?.abort();
    const ac = new AbortController();
    savedFetchRef.current = ac;

    const timer = setTimeout(async () => {
      if (loadingRef.current) return;
      try {
        const saved = await getSavedPageSpeed(trimmed, { signal: ac.signal });
        setResult((prev) => {
          if (
            prev?.source === "live" &&
            normalizeUrlForCompare(prev.url || "") ===
              normalizeUrlForCompare(trimmed)
          ) {
            return prev;
          }
          return { ...saved, source: "db" };
        });
      } catch (err) {
        if (isCancel(err)) return;
        if (err.response?.status === 404) {
          setResult((prev) => {
            if (
              prev?.source === "live" &&
              normalizeUrlForCompare(prev.url || "") ===
                normalizeUrlForCompare(trimmed)
            ) {
              return prev;
            }
            return null;
          });
        }
      }
    }, 650);

    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [url]);

  const bundle = result?.[strategy] ?? null;
  const hasBundle = !!bundle;

  const displayCategories = hasBundle
    ? {
        performance: bundle.categories?.performance ?? null,
        accessibility: bundle.categories?.accessibility ?? null,
        bestPractices: bundle.categories?.bestPractices ?? null,
        seo: bundle.categories?.seo ?? null,
      }
    : ZERO_CATS;

  const perfScore = displayCategories.performance;

  const activeAudits = useMemo(() => {
    if (!bundle) return [];
    const abc = bundle.auditsByCategory;
    if (abc && typeof abc === "object") {
      const list = abc[insightCategory];
      if (Array.isArray(list)) return sortAudits(list);
    }
    if (insightCategory === "seo" && Array.isArray(bundle.audits)) {
      return sortAudits(bundle.audits);
    }
    return [];
  }, [bundle, insightCategory]);

  const clientAuditInsights = useMemo(() => {
    const total = activeAudits.length;
    if (total === 0) return { items: [], total: 0, truncated: false };
    const truncated = total > CLIENT_AUDIT_INSIGHT_LIMIT;
    const items = truncated
      ? activeAudits.slice(0, CLIENT_AUDIT_INSIGHT_LIMIT)
      : activeAudits;
    return { items, total, truncated };
  }, [activeAudits]);

  const metricsOrdered = useMemo(() => {
    if (bundle?.metrics?.length) return orderMetrics(bundle.metrics);
    return buildPlaceholderMetrics(t);
  }, [bundle?.metrics, t]);

  const partialErr = result?.partialErrors?.[strategy];
  const lastChecked = result?.lastCheckedAt;

  const displayUrl =
    bundle?.url || result?.url || t("seoPage.noUrlYet");

  const hasPlans = plans.length > 0;
  const carouselInactive = hasPlans && inputSource === "custom";
  const urlAsideInactive = hasPlans && inputSource === "project";

  const categoryStrip = (
    <div
      className={styles.categoryStrip}
      role="tablist"
      aria-label={t("seoPage.categoryStripAria")}
    >
      {INSIGHT_ORDER.map((cat) => {
        const score = displayCategories[cat];
        const active = insightCategory === cat;
        return (
          <button
            key={cat}
            type="button"
            role="tab"
            id={`psi-tab-${cat}`}
            aria-selected={active}
            className={`${styles.categoryStripBtn} ${
              active ? styles.categoryStripBtnActive : ""
            }`}
            onClick={() => selectInsightCategory(cat)}
          >
            <span className={styles.categoryItem}>
              <PsiRing
                score={score}
                size="small"
                ariaLabel={`${t(INSIGHT_LABEL[cat])}: ${score ?? "—"}`}
              />
              <span className={styles.categoryName}>{t(INSIGHT_LABEL[cat])}</span>
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={portalStyles.portalPage}>
      <div className={portalStyles.container}>
        <PortalShellPageHeader
          icon={<Search size={28} strokeWidth={1.75} absoluteStrokeWidth />}
          title={t("seoPage.title")}
        >
          <div className={styles.pageSeoIntro}>
            <p className={styles.pageSeoIntroText}>{t("seoPage.introShort")}</p>
          </div>
        </PortalShellPageHeader>

        <section className={styles.inputCard}>
          <form
            className={styles.inputCardForm}
            onSubmit={(e) => {
              e.preventDefault();
              run();
            }}
          >
            {hasPlans ? (
              <div
                className={styles.sourceRow}
                role="radiogroup"
                aria-label={t("seoPage.inputSourceAria")}
              >
                <span className={styles.sourceRowLead}>
                  {t("seoPage.inputSourceLead")}
                </span>
                <div className={styles.sourceToggle}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={inputSource === "project"}
                    className={`${styles.sourceBtn} ${styles.sourceBtnWithIcon} ${
                      inputSource === "project" ? styles.sourceBtnActive : ""
                    }`}
                    onClick={() => setInputSource("project")}
                  >
                    <FolderKanban
                      className={styles.sourceBtnIcon}
                      size={16}
                      strokeWidth={2}
                      aria-hidden
                    />
                    {t("seoPage.inputSourceProject")}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={inputSource === "custom"}
                    className={`${styles.sourceBtn} ${styles.sourceBtnWithIcon} ${
                      inputSource === "custom" ? styles.sourceBtnActive : ""
                    }`}
                    onClick={() => setInputSource("custom")}
                  >
                    <Link2
                      className={styles.sourceBtnIcon}
                      size={16}
                      strokeWidth={2}
                      aria-hidden
                    />
                    {t("seoPage.inputSourceCustom")}
                  </button>
                </div>
              </div>
            ) : null}

            <div className={`${styles.pickerRow} ${styles.pickerRowTwoCol}`}>
              <section
                className={`${styles.carouselSection} ${
                  carouselInactive ? styles.pickerRegionInactive : ""
                }`}
                aria-label={t("seoPage.projectsCarouselAria")}
                inert={carouselInactive || undefined}
              >
                <div className={styles.carouselWrap}>
                  <ProjectPickerCarousel
                    embedded
                    fullWidthEmbeddedViewport
                    matchProductsHeight
                    plans={plans}
                    selectedPlanId={selectedProjectId}
                    onSelect={setSelectedProjectId}
                  />
                </div>
              </section>
              <aside
                className={`${styles.urlAside} ${
                  urlAsideInactive ? styles.pickerRegionInactive : ""
                }`}
                aria-labelledby="seo-form-heading"
                inert={urlAsideInactive || undefined}
              >
                <h2 id="seo-form-heading" className={styles.formHeading}>
                  {t("seoPage.formHeading")}
                </h2>
                <div className={styles.form}>
                  <label className={styles.label} htmlFor="seo-url">
                    {t("seoPage.urlLabel")}
                  </label>
                  <input
                    id="seo-url"
                    type="url"
                    className={styles.input}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder={t("seoPage.urlPlaceholder")}
                    autoComplete="url"
                    spellCheck={false}
                    readOnly={inputSource === "project" && hasPlans}
                  />
                  <p className={styles.hint}>{t("seoPage.customUrlHelp")}</p>
                  <p className={styles.hint}>{t("seoPage.analyzeBothHint")}</p>
                </div>
                <p className={styles.poweredBy}>
                  <GaugeCircle
                    className={styles.poweredByGlyph}
                    size={15}
                    strokeWidth={1.85}
                    aria-hidden
                  />
                  <span>
                    {t("seoPage.poweredByPrefix")}{" "}
                    <a
                      href="https://developers.google.com/speed/docs/insights/v5/get-started"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.poweredByLink}
                    >
                      {t("seoPage.poweredByLinkText")}
                    </a>
                    {t("seoPage.poweredBySuffix")}
                  </span>
                </p>
              </aside>
            </div>

            <div
              className={styles.deviceRow}
              role="group"
              aria-label={t("seoPage.strategyLabel")}
            >
              <span className={styles.deviceRowLabel}>
                <MonitorSmartphone
                  className={styles.deviceRowLabelIcon}
                  size={18}
                  strokeWidth={1.75}
                  aria-hidden
                />
                {t("seoPage.strategyLabel")}
              </span>
              <div className={styles.strategyToggle}>
                <button
                  type="button"
                  className={`${styles.strategyBtn} ${styles.strategyBtnWithIcon} ${
                    strategy === "mobile" ? styles.strategyBtnActive : ""
                  }`}
                  onClick={() => setStrategy("mobile")}
                  aria-pressed={strategy === "mobile"}
                >
                  <Smartphone
                    className={styles.strategyBtnIcon}
                    size={16}
                    strokeWidth={2}
                    aria-hidden
                  />
                  {t("seoPage.strategyMobile")}
                </button>
                <button
                  type="button"
                  className={`${styles.strategyBtn} ${styles.strategyBtnWithIcon} ${
                    strategy === "desktop" ? styles.strategyBtnActive : ""
                  }`}
                  onClick={() => setStrategy("desktop")}
                  aria-pressed={strategy === "desktop"}
                >
                  <Monitor
                    className={styles.strategyBtnIcon}
                    size={16}
                    strokeWidth={2}
                    aria-hidden
                  />
                  {t("seoPage.strategyDesktop")}
                </button>
              </div>
            </div>

            <div className={styles.submitRow}>
              <Button type="submit" disabled={loading}>
                {loading ? t("seoPage.analyzing") : t("seoPage.run")}
              </Button>
            </div>
          </form>
        </section>

        {error && (
          <div className={styles.alertError} role="alert">
            {error}
          </div>
        )}

        <article
          className={`${styles.reportCard} ${loading ? styles.reportLoading : ""}`}
          aria-live="polite"
          aria-busy={loading}
        >
          {result?.partialErrors &&
            (result.partialErrors.mobile || result.partialErrors.desktop) && (
              <div className={styles.alertWarn} role="status">
                {result.partialErrors.mobile && (
                  <div>
                    {t("seoPage.partialMobile")} {result.partialErrors.mobile}
                  </div>
                )}
                {result.partialErrors.desktop && (
                  <div>
                    {t("seoPage.partialDesktop")}{" "}
                    {result.partialErrors.desktop}
                  </div>
                )}
              </div>
            )}

          {result && !bundle && partialErr && (
            <div className={styles.alertError} role="status">
              {(strategy === "mobile"
                ? t("seoPage.strategyFailedMobile")
                : t("seoPage.strategyFailedDesktop")) +
                ": " +
                partialErr}
            </div>
          )}

          {bundle?.runtimeError?.message && (
            <div className={styles.alertWarn} role="status">
              <strong>{t("seoPage.lighthouseRuntimeError")}</strong>{" "}
              {bundle.runtimeError.message}
            </div>
          )}

          {categoryStrip}

          <div
            ref={reportDetailRef}
            className={styles.reportDetailBlock}
            role="tabpanel"
            id={`psi-panel-${insightCategory}`}
            aria-labelledby={`psi-tab-${insightCategory}`}
          >
            {insightCategory === "performance" ? (
              <div
                className={`${styles.heroRow} ${
                  strategy === "desktop" ? styles.heroRowDesktopPreview : ""
                }`}
              >
                <div className={styles.heroLeft}>
                  <PsiRing
                    score={perfScore}
                    size="large"
                    label={t("seoPage.heroPerformance")}
                    ariaLabel={`${t("seoPage.heroPerformance")}: ${perfScore ?? "—"}`}
                  />
                  <p className={styles.disclaimer}>{t("seoPage.disclaimer")}</p>
                  <div className={styles.legend}>
                    <div className={styles.legendItem}>
                      <span className={styles.legendShapePoor} aria-hidden />
                      <span>
                        {t("seoPage.legendPoor")} ({t("seoPage.legendPoorLabel")})
                      </span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={styles.legendShapeAvg} aria-hidden />
                      <span>
                        {t("seoPage.legendAvg")} ({t("seoPage.legendAvgLabel")})
                      </span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={styles.legendShapeGood} aria-hidden />
                      <span>
                        {t("seoPage.legendGood")} ({t("seoPage.legendGoodLabel")})
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.heroRight} ${
                    strategy === "desktop" ? styles.heroRightDesktopStand : ""
                  }`}
                >
                  <div
                    className={
                      strategy === "desktop"
                        ? styles.desktopFrame
                        : styles.phoneFrame
                    }
                  >
                    <div
                      className={
                        strategy === "desktop"
                          ? styles.desktopScreen
                          : styles.phoneScreen
                      }
                    >
                      {bundle?.screenshot || bundle?.screenshotUrl ? (
                        <img
                          src={bundle.screenshot || bundle.screenshotUrl}
                          alt=""
                          className={styles.previewShot}
                        />
                      ) : (
                        <div className={styles.previewPlaceholder} />
                      )}
                    </div>
                  </div>
                  {result?.source === "db" &&
                    bundle &&
                    !bundle.screenshot &&
                    !bundle.screenshotUrl && (
                    <p
                      className={`${styles.screenshotHint} ${
                        strategy === "desktop"
                          ? styles.screenshotHintDesktop
                          : ""
                      }`}
                    >
                      {t("seoPage.screenshotDbHint")}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.categoryOnlyHero}>
                <PsiRing
                  score={displayCategories[insightCategory]}
                  size="large"
                  label={t(INSIGHT_LABEL[insightCategory])}
                  ariaLabel={`${t(INSIGHT_LABEL[insightCategory])}: ${displayCategories[insightCategory] ?? "—"}`}
                />
                <p className={styles.categoryOnlyDisclaimer}>
                  {t("seoPage.categoryInsightDisclaimer")}
                </p>
              </div>
            )}

            <p className={styles.resultMeta}>
              <span className={styles.resultUrl}>{displayUrl}</span>
              {bundle?.fetchTime && (
                <span className={styles.fetchTime}>
                  {" · "}
                  {t("seoPage.analyzedAt", {
                    time: new Date(bundle.fetchTime).toLocaleString(),
                  })}
                </span>
              )}
              {lastChecked && (
                <span className={styles.lastChecked}>
                  {" · "}
                  {t("seoPage.lastCheckedStored", {
                    time: new Date(lastChecked).toLocaleString(),
                  })}
                </span>
              )}
            </p>

            {insightCategory === "performance" ? (
              <>
                <div className={styles.metricsHead}>
                  <h3 className={styles.metricsTitle}>
                    {t("seoPage.metricsHeading")}
                  </h3>
                </div>
                <div className={styles.metricsGrid}>
                  {metricsOrdered.map((m) => {
                    const tier = scoreTierAudit(m.score, m.scoreDisplayMode);
                    return (
                      <div key={m.id} className={styles.metricCell}>
                        <MetricDot tier={tier} />
                        <span className={styles.metricName}>{m.title}</span>
                        <span className={styles.metricValue}>
                          {m.displayValue ?? "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}

            <section
              className={styles.categoryInsights}
              aria-labelledby="psi-audits-heading"
            >
              <h3 id="psi-audits-heading" className={styles.categoryInsightsTitle}>
                {t(INSIGHT_AUDITS_TITLE[insightCategory])}
              </h3>
              {activeAudits.length > 0 ? (
                <>
                  <ul className={styles.auditList}>
                    {clientAuditInsights.items.map((a) => {
                      const tier = scoreTierAudit(a.score, a.scoreDisplayMode);
                      return (
                        <li key={a.id} className={styles.auditItem}>
                          <span
                            className={`${styles.auditIconWrap} ${styles[`auditIcon_${tier}`]}`}
                          >
                            <AuditInsightIcon tier={tier} />
                          </span>
                          <div className={styles.auditItemBody}>
                            <div className={styles.auditHead}>
                              <span className={styles.auditTitle}>{a.title}</span>
                              {a.displayValue && (
                                <span className={styles.auditMetric}>
                                  {a.displayValue}
                                </span>
                              )}
                              <span
                                className={`${styles.badge} ${styles[`badge_${tier}`]}`}
                              >
                                {tier === "good" && t("seoPage.badge.pass")}
                                {tier === "avg" && t("seoPage.badge.average")}
                                {tier === "poor" && t("seoPage.badge.fail")}
                                {tier === "neutral" && t("seoPage.badge.info")}
                              </span>
                            </div>
                            {a.description && (
                              <p className={styles.auditDesc}>
                                {htmlToText(a.description)}
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {clientAuditInsights.truncated ? (
                    <p className={styles.auditPreviewNote}>
                      {t("seoPage.categoryInsightsPreviewNote", {
                        shown: clientAuditInsights.items.length,
                        total: clientAuditInsights.total,
                      })}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className={styles.emptySeo}>
                  {t("seoPage.categoryInsightsEmpty")}
                </p>
              )}
            </section>
          </div>
        </article>
      </div>
    </div>
  );
}
